import {
  createTheme,
  darken,
  lighten,
  alpha,
} from '@mui/material/styles';
import { format as formatDate } from 'date-fns';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import variables from './styleVariables';

export const roundedBorderBackground = (
  side: 'top' | 'right' | 'bottom' | 'left' = 'left',
  borderColor: string = variables.primary1,
  backgroundColor: string = 'white',
  width: number = 4,
): string => {
  const angles = {
    bottom: '0',
    left: '90',
    right: '270',
    top: '180',
  };

  return `
      linear-gradient(${angles[side]}deg,
      ${borderColor} 0,
      ${borderColor} ${width}px,
      ${backgroundColor} ${width}px,
      ${backgroundColor} 100%)`;
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    fontWeightBlack: number;
  }

  interface TypographyVariantsOptions {
    fontWeightBlack?: number;
  }

  interface Palette {
    backgroundPrimary: string;
    backgroundSecondary: string;
    backgroundTertiary: string;
    backgroundFour: string;
    grey1: string;
    grey2: string;
    grey3: string;
    grey4: string;
    grey5: string;
    grey6: string;
    primary1: string;
    primary2: string;
    primary3: string;
    primary4: string;
    primary5: string;
    primary6: string;
    tertiary1: string;
    tertiary2: string;
    tertiary3: string;
    tertiary4: string;
    tertiary5: string;
    tertiary6: string;
    tertiary7: string;
    tertiary8: string;
    textHint: string;
    textTable: string;
  }

  interface PaletteOptions {
    backgroundPrimary: string;
    backgroundSecondary: string;
    backgroundTertiary: string;
    backgroundFour: string;
    grey1: string;
    grey2: string;
    grey3: string;
    grey4: string;
    grey5: string;
    grey6: string;
    primary1: string;
    primary2: string;
    primary3: string;
    primary4: string;
    primary5: string;
    primary6: string;
    tertiary1: string;
    tertiary2: string;
    tertiary3: string;
    tertiary4: string;
    tertiary5: string;
    tertiary6: string;
    tertiary7: string;
    tertiary8: string;
    textHint: string;
    textTable: string;
  }
}

export const theme = createTheme({
  components: {
    MuiBadge: {
      styleOverrides: {
        colorPrimary: {
          color: 'white',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0px 3px 6px #00000029',
        },
        startIcon: {
          color: variables.primary1,
        },
        text: {
          backgroundColor: 'white',
          paddingLeft: '1rem',
          paddingRight: '1rem',
        },
        outlined: {
          backgroundColor: 'white',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: variables.primary4,
          color: 'white',
          '& .MuiButton-startIcon': {
            color: 'white',
          },
          '&:hover': {
            backgroundColor: variables.grey4,
          },
        },
        containedPrimary: {
          backgroundColor: variables.primary1,
          color: 'white',
          '& .MuiButton-startIcon': {
            color: 'white',
          },
          '&:hover': {
            backgroundColor: darken(variables.primary1, 0.12),
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '1rem',
        },
        subheader: {
          color: variables.primary4,
        },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'none' },
    },
    MuiTextField: {
      defaultProps: { variant: 'standard' },
    },
    MuiFormControl: {
      defaultProps: { variant: 'standard' },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: variables.primary1,
          color: 'white',
        },
        primary: {
          color: 'white',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: variables.primary1,
          color: 'white',
        },
        colorSecondary: {
          backgroundColor: variables.tertiary5,
          color: variables.primary4,
        },
        root: {
          '&.MuiChip-filled.MuiChip-filledDefault': {
            backgroundColor: variables.grey6,
            color: variables.textPrimary,
          },
          '&.MuiChip-filled': {
            backgroundColor: variables.grey6,
          },
          marginRight: '0.25rem',
          '& .MuiChip-avatarColorPrimary': {
            color: 'white',
            backgroundColor: variables.primary4,
          },
          maxWidth: '100%',
          '& .MuiChip-avatarColorSecondary': {
            backgroundColor: variables.primary4,
          },
          '.MuiAutocomplete-root &': {
            boxShadow:
              '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)', // theme.shadows[3]
            margin: '0.5rem 1rem 0.5rem 0',
          },
        },
        label: {
          display: 'block',
          alignItems: 'center',
          textOverflow: 'ellipsis',
        },
        deleteIcon: {
          color: variables.textPrimary,
          '&:hover': {
            color: darken(variables.textPrimary, 1),
          },
        },
        deleteIconColorPrimary: {
          color: 'white',
          '&:hover': {
            color: variables.grey6,
          },
        },
        deletableColorPrimary: {
          '&:focus': {
            backgroundColor: lighten(variables.primary1, 0.3),
          },
        },
        deleteIconColorSecondary: {
          color: variables.primary4,
          '&:hover': {
            color: darken(variables.primary4, 0.2),
          },
        },
        deletableColorSecondary: {
          '&:focus': {
            backgroundColor: lighten(variables.tertiary5, 0.3),
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          padding: '0.75rem 0',
        },
        underline: {
          '&::before': {
            borderBottom: `2px solid ${variables.primary1}`,
          },
          '&:hover:not(.Mui-disabled)::before': {
            borderBottom: `2px solid ${variables.ruleColor}`,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        multiline: { paddingBottom: 0 },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: variables.textSecondary,
          fontSize: '1.25rem',
          fontWeight: 500,
          '& + .MuiFormControl-root .MuiInputBase-root': {
            marginTop: '0.5rem',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 0,
          '& option': {
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600',
            padding: '0.5rem',
          },
          '& option[aria-selected="true"]': {
            color: variables.primary1,
          },
          '& option:not(:last-child)': {
            borderBottom: `1px solid ${variables.ruleColor}`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: variables.primary1,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: variables.ruleColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: variables.ruleColor,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&:not(.hasDefaultScrollBar)::-webkit-scrollbar': {
            display: 'none',
            width: 0,
            background: 'transparent',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          alignSelf: 'start',
        },
      },
    },
    MuiAvatarGroup: {
      defaultProps: {
        spacing: 12,
      },
      styleOverrides: {
        root: ({ theme: t }) => ({
          '&.MuiAvatarGroup-root .MuiAvatarGroup-avatar': {
            border: `2px solid ${t.palette.background.default}`,
            boxSizing: 'content-box',
            marginLeft: 'var(--AvatarGroup-spacing, -12px) !important',
          },
          '&.MuiAvatarGroup-root .MuiAvatarGroup-avatar:last-child': {
            marginLeft: '0 !important',
          },
          '&.MuiAvatarGroup-root .MuiAvatarGroup-avatar .MuiAvatar-root': {
            border: 'none',
            marginLeft: '0 !important',
            boxSizing: 'border-box',
          },
        }),
      },
    },
    MuiDateCalendar: {
      defaultProps: {
        dayOfWeekFormatter: (value: Date) => formatDate(value, 'EEEEEE'),
      },
    },
    MuiDatePickerToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: variables.primary1,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          padding: '12px 24px 16px',
          '& .MuiTypography-overline': {
            color: alpha('#ffffff', 0.85),
          },
          '& .MuiPickersToolbar-title, & .MuiDatePickerToolbar-title': {
            color: 'white',
            fontWeight: 500,
          },
          '& .MuiIconButton-root': {
            color: 'white',
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: variables.primary1,
            color: 'white',
            '&:hover, &:focus, &.Mui-selected:hover, &.Mui-selected:focus': {
              backgroundColor: variables.primary1,
            },
          },
          '&:not(.Mui-selected).MuiPickersDay-today': {
            borderColor: variables.primary1,
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          paddingLeft: 12,
          paddingRight: 12,
          marginTop: 8,
          marginBottom: 8,
        },
        labelContainer: {
          gridColumn: 2,
          gridRow: 1,
          justifyContent: 'center',
          margin: 0,
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        root: {
          display: 'contents',
          '& > button:first-of-type': {
            gridColumn: 1,
            gridRow: 1,
            justifySelf: 'start',
          },
          '& > button:last-of-type': {
            gridColumn: 3,
            gridRow: 1,
            justifySelf: 'end',
          },
        },
        spacer: {
          display: 'none',
        },
      },
    },
    MuiSelect: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        select: {
          outline: 'none',
          padding: '0.75rem 1rem',
          '&:focus': { backgroundColor: 'transparent' },
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          '&.stepPriorToCurrent:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          border: '1px solid white',
          borderRadius: '50%',
          color: variables.grey4,
          '&.Mui-active': {
            color: 'white',
            border: `1px solid ${variables.primary1}`,
            boxShadow: `0 2px 8px ${alpha(variables.primary1, 0.4)}`,
            '& text': {
              fill: variables.primary1,
            },
          },
          '&.Mui-completed': {
            color: variables.grey4,
          },
        },
        text: {
          fill: 'white',
          fontWeight: 700,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          lineHeight: 'normal',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '&.MuiPaper-root': {
            background: 'none',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          position: 'relative',
          zIndex: 1,
        },
        root: {
          borderBottom: `1px solid ${variables.ruleColor}`,
          height: 'fit-content',
        },
        indicator: {
          backgroundColor: variables.primary1,
          height: 4,
        },
        scroller: {
          '.content__sub &.MuiTabs-scrollable:nth-of-type(2n)': {
            paddingLeft: '1rem',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: '0 0.5rem',
          '&:not(:last-child)': {
            marginRight: '0.5rem',
          },
          [`@media (min-width: ${variables.breakpointSm}px)`]: {
            minWidth: 0,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          lineHeight: 'normal',
          textTransform: 'uppercase',
        },
        root: {
          borderBottom: `1px solid ${variables.ruleColor}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          verticalAlign: 'bottom',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        overline: {
          display: 'block',
          fontSize: '0.625rem',
          lineHeight: 'normal',
          marginBottom: '0.5rem',
        },
        h1: { fontWeight: 500 },
        h2: { fontWeight: 500 },
        h3: { fontWeight: 500 },
        h4: { fontWeight: 500 },
        h5: { fontWeight: 500 },
        h6: { fontWeight: 500 },
      },
    },
  },
  breakpoints: {
    values: {
      xs: variables.breakpointXs,
      sm: variables.breakpointSm,
      md: variables.breakpointMd,
      lg: variables.breakpointLg,
      xl: variables.breakpointXl,
    },
  },
  palette: {
    backgroundPrimary: variables.backgroundPrimary,
    backgroundSecondary: variables.backgroundSecondary,
    backgroundTertiary: variables.backgroundTertiary,
    backgroundFour: variables.backgroundFour,
    divider: variables.ruleColor,
    error: {
      main: variables.error,
    },
    grey1: variables.grey1,
    grey2: variables.grey2,
    grey3: variables.grey3,
    grey4: variables.grey4,
    grey5: variables.grey5,
    grey6: variables.grey6,
    primary: {
      main: variables.primary1,
    },
    primary1: variables.primary1,
    primary2: variables.primary2,
    primary3: variables.primary3,
    primary4: variables.primary4,
    primary5: variables.primary5,
    primary6: variables.primary6,
    success: {
      main: variables.success,
    },
    text: {
      primary: variables.textPrimary,
      secondary: variables.textSecondary,
    },
    textHint: variables.textHint,
    tertiary1: variables.tertiary1,
    tertiary2: variables.tertiary2,
    tertiary3: variables.tertiary3,
    tertiary4: variables.tertiary4,
    tertiary5: variables.tertiary5,
    tertiary6: variables.tertiary6,
    tertiary7: variables.tertiary7,
    tertiary8: variables.tertiary8,
    textTable: variables.textTable,
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: variables.sansSerif,
    fontWeightBlack: 900,
  },
});

export default theme;
